/**
 * Màn **Kết nối** — mọi đường ra ngoài: nhập được, lưu được, và **biết đã nối hay chưa**.
 *
 * Viết lại 10/09/2026. Bản trước chỉ dán được ba khoá AI; tài khoản Website IME và token Facebook
 * **không có ô nhập nào** — muốn đổi thì phải SSH vào máy chủ sửa `.env`. Và kết quả "Kiểm tra
 * thật" bị vứt ngay sau khi hiện, nên mở lại màn là mất.
 *
 * Bốn luật của màn này:
 *
 * 1. **Không bao giờ hiện một phần bí mật** — trừ **bốn ký tự cuối** của giá trị đã lưu, và nó có
 *    mặt vì dán nhầm khoá của dự án khác là chuyện xảy ra thật.
 * 2. **`nguon` quan trọng hơn `co`.** Biết "đã có" thì ít giá trị hơn biết giá trị đang dùng đến
 *    từ đâu: một giá trị hỏng dán qua dashboard **che mất** giá trị đúng trong `.env`, và triệu
 *    chứng là 401 chứ không phải "thiếu cấu hình".
 * 3. **Chưa kiểm bao giờ ≠ kiểm rồi và hỏng.** Hai trạng thái, hai cách hiện (`CLAUDE.md` §5.14).
 * 4. **Đường chưa có code đọc tới phải tự khai.** Một dấu tích xanh cho tính năng chưa tồn tại là
 *    lời nói dối tốn nhất — nó khiến người ta ngừng tìm nguyên nhân thật.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { duNhapTay } from "@autoime/shared";

import { api, type HangKetNoiUI, type KetQuaKiem, type TruongKetNoiUI } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { gioVN } from "../lib/nhan.ts";
import { useToast } from "../thanh-phan/toast.tsx";
import {
  Bao,
  Chip,
  DangTai,
  DauThe,
  DauTrang,
  Khoi,
  Nut,
  The,
  HangNut,
  ViSao,
  lopO,
} from "../thanh-phan/giao-dien.tsx";

export function KetNoi() {
  const kho = useQueryClient();
  const { data, isPending, error } = useQuery({ queryKey: khoa.ketNoi, queryFn: api.ketNoi });

  if (error) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Kết nối" />
        <Bao muc="loi" tieuDe="Không tải được">
          {error.message}
        </Bao>
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Kết nối" />
        <DangTai />
      </div>
    );
  }

  const chuaDu = data.hang.filter((h) => h.truong.some((t) => t.batBuoc) && !h.daDuCauHinh);

  return (
    <div className="space-y-4">
      <DauTrang
        tieuDe="Kết nối"
        moTa="Mọi đường AutoIME đi ra ngoài. Giá trị lưu mã hoá, không đọc lại được — chỉ dán đè."
      />

      {!data.coKhoaMaHoa && (
        <Bao muc="loi" tieuDe="Thiếu TOKEN_ENCRYPTION_KEY — không lưu được bí mật nào">
          Không có khoá mã hoá thì AutoIME <strong>từ chối lưu</strong> thay vì lưu trần. Sinh một
          khoá rồi thêm vào <code>.env</code> và khởi động lại:
          <pre className="mt-2 overflow-x-auto rounded-tile bg-muc-100 p-2 text-xs">
            node -e "console.log(require('crypto').randomBytes(32).toString('base64'))"
          </pre>
          Tạo <strong>một lần</strong> — đổi là mọi bí mật cũ giải mã ra rác.
        </Bao>
      )}

      {chuaDu.length > 0 && (
        <Bao muc="canh" tieuDe={`${chuaDu.length} đường chưa đủ cấu hình`}>
          {chuaDu.map((h) => h.ten).join(" · ")}
        </Bao>
      )}

      <Bao muc={data.publishMode === "live" ? "canh" : "thuong"} tieuDe={`PUBLISH_MODE = ${data.publishMode}`}>
        {data.publishMode === "live"
          ? "Bài sẽ ĐĂNG THẬT lên website khách hàng. Biến này chỉ đổi được trong .env."
          : "Không đăng gì ra ngoài. Đổi trong .env rồi khởi động lại mới có hiệu lực."}
      </Bao>

      {data.hang.map((h) => (
        <TheKetNoi key={h.khoa} h={h} onXong={() => void kho.invalidateQueries({ queryKey: khoa.ketNoi })} />
      ))}

      <Khoi nhan="Model đang dùng cho bốn tác vụ chính" id="ket-noi-model">
        <ul className="space-y-1 text-sm">
          {data.modelDangDung.map((m) => (
            <li key={m.tacVu} className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium text-muc-800">{m.tacVu}</span>
              <span className="text-muc-600">{m.catalogKey}</span>
              <Chip muc={m.tonTien ? "thuong" : "canh"}>
                {m.tonTien ? m.nhaCungCap : "model đóng thế — $0"}
              </Chip>
            </li>
          ))}
        </ul>
        <ViSao>
          Model đóng thế nghĩa là <strong>không chạm mạng và không tốn tiền</strong>. Cả dây chuyền
          vẫn chạy đủ; bài ra là bài mẫu chứ không phải văn. Đổi model ở màn Cài đặt.
        </ViSao>
      </Khoi>

      <Khoi nhan="Công tắc đăng từng site" id="ket-noi-site">
        <div className="flex flex-wrap gap-2">
          {data.congTacSite.map((s) => (
            <Chip key={s.siteSlug} muc={s.bat ? "on" : "thuong"}>
              {s.siteSlug} · {s.bat ? "BẬT" : "tắt"}
            </Chip>
          ))}
        </div>
        <ViSao>
          Bật từng site một, không bật cả năm cùng lúc (`workflow.md` §5.1). Đổi ở màn Cài đặt,
          khoá <code>publish.&lt;site&gt;</code>.
        </ViSao>
      </Khoi>
    </div>
  );
}

/* ═══ Một đường kết nối ═══════════════════════════════════════════════ */

function TheKetNoi({ h, onXong }: { h: HangKetNoiUI; onXong: () => void }) {
  const toast = useToast();
  const [nhap, datNhap] = useState<Record<string, string>>({});

  const luu = useMutation({
    mutationFn: () => api.ketNoiLuu(h.khoa, nhap),
    onSuccess: (kq) => {
      if (kq.daLuu.length === 0) {
        toast.bao("thuong", "Không có gì để lưu", "Mọi ô đang để trống.");
        return;
      }
      datNhap({});
      if (kq.kiem?.ok) toast.on(`${h.ten} — đã nối`, kq.kiem.chiTiet);
      else if (kq.kiem) toast.loi(`Đã lưu, nhưng KHÔNG nối được`, kq.kiem.loi ?? undefined);
      // Lưu xong mà chưa kiểm được thì thứ cần nói là BƯỚC TIẾP THEO, không phải số trường.
      else if (kq.buocTiep) toast.bao("thuong", "Đã lưu — còn một bước", kq.buocTiep);
      else toast.on("Đã lưu", `${kq.daLuu.length} trường.`);
      onXong();
    },
    onError: (loi: Error) => toast.loi("Không lưu được", loi.message),
  });

  const kiem = useMutation({
    mutationFn: () => api.ketNoiKiem(h.khoa),
    onSuccess: (kq) => {
      if (kq.ok) toast.on(`${h.ten} — đã nối`, kq.chiTiet);
      else toast.loi(`${h.ten} — chưa nối được`, kq.loi ?? undefined);
      onXong();
    },
    onError: (loi: Error) => toast.loi("Không kiểm được", loi.message),
  });

  const xoa = useMutation({
    mutationFn: (truong: string) => api.ketNoiXoaTruong(h.khoa, truong),
    onSuccess: () => {
      toast.bao("thuong", "Đã xoá khỏi database", "Giá trị trong .env (nếu có) lại thành nguồn.");
      onXong();
    },
    onError: (loi: Error) => toast.loi("Không xoá được", loi.message),
  });

  const dangBan = luu.isPending || kiem.isPending || xoa.isPending;

  return (
    <The vien={h.kiem?.ok ? "on" : h.kiem && !h.kiem.ok ? "loi" : "thuong"}>
      <DauThe tieuDe={h.ten} phu={h.moTa}>
        <HuyHieuKetNoi h={h} />
      </DauThe>

      {h.chuaDung && (
        <Bao muc="canh" tieuDe="Lưu được, nhưng chưa dùng tới">
          {h.chuaDung}
        </Bao>
      )}

      <div className="mt-3 space-y-3">
        {h.truong
          .filter((t) => !t.tuSinh)
          .map((t) => (
            <OTruong
              key={t.khoa}
              t={t}
              giaTri={nhap[t.khoa] ?? ""}
              onDoi={(v) => datNhap((cu) => ({ ...cu, [t.khoa]: v }))}
              onXoa={() => xoa.mutate(t.khoa)}
              khoaHet={dangBan}
            />
          ))}
      </div>

      {h.khoa === "facebook" && <KhoiFacebook h={h} onXong={onXong} khoaHet={dangBan} />}

      <HangNut>
        <Nut
          kieu="vien"
          dangChay={luu.isPending}
          disabled={dangBan || Object.values(nhap).every((v) => v.trim() === "")}
          onClick={() => luu.mutate()}
        >
          Lưu{h.kiemDuoc ? " và kiểm" : ""}
        </Nut>
        {h.kiemDuoc && (
          <Nut kieu="phang" dangChay={kiem.isPending} disabled={dangBan} onClick={() => kiem.mutate()}>
            Kiểm lại
          </Nut>
        )}
      </HangNut>

      <KetQuaKiemHien kiem={h.kiem} kiemDuoc={h.kiemDuoc} />

      <ViSao nhan="thiếu thì sao">{h.thieuThiSao}</ViSao>
      <ViSao nhan="lấy ở đâu">{h.huongDan}</ViSao>
    </The>
  );
}

function HuyHieuKetNoi({ h }: { h: HangKetNoiUI }) {
  // "Chưa đặt gì" phải đứng TRƯỚC "đủ cấu hình": một đường không có trường bắt buộc nào thì
  // `daDuCauHinh` đúng theo logic mà sai theo nghĩa — nó báo "đủ" cho một đường trống trơn.
  if (!h.coGiaTriNao) return <Chip muc="thuong">chưa đặt</Chip>;
  // "Người chưa gõ" và "máy chưa sinh" là hai chuyện khác nhau: cái đầu là thiếu, cái sau là
  // đang làm dở. Tô đỏ cả hai thì cái đỏ không còn nghĩa gì.
  if (!h.daDuCauHinh) {
    return duNhapTay(h.truong) ? (
      <Chip muc="canh">còn một bước</Chip>
    ) : (
      <Chip muc="loi">chưa đủ cấu hình</Chip>
    );
  }
  if (!h.kiemDuoc) return <Chip muc="thuong">đã lưu · không kiểm tự động được</Chip>;
  // Ba trạng thái, không phải hai: chưa kiểm bao giờ KHÁC kiểm rồi và hỏng.
  if (!h.kiem) return <Chip muc="canh">chưa kiểm lần nào</Chip>;
  return h.kiem.ok ? <Chip muc="on">đã nối</Chip> : <Chip muc="loi">không nối được</Chip>;
}

function KetQuaKiemHien({ kiem, kiemDuoc }: { kiem: KetQuaKiem | null; kiemDuoc: boolean }) {
  if (!kiemDuoc) return null;
  if (!kiem) {
    return (
      <p className="mt-3 text-sm text-muc-500">
        Chưa kiểm lần nào — <strong>không phải</strong> "đã kiểm và không sao".
      </p>
    );
  }
  return (
    <div
      className={`mt-3 rounded-tile border p-3 text-sm ${
        kiem.ok ? "border-on-200 bg-on-50 text-on-700" : "border-loi-200 bg-loi-50 text-loi-700"
      }`}
    >
      <div className="font-medium">{kiem.ok ? kiem.chiTiet : kiem.loi}</div>
      <div className="mt-1 text-xs opacity-80">kiểm lúc {gioVN(kiem.luc)}</div>
    </div>
  );
}

function OTruong({
  t,
  giaTri,
  onDoi,
  onXoa,
  khoaHet,
}: {
  t: TruongKetNoiUI;
  giaTri: string;
  onDoi: (v: string) => void;
  onXoa: () => void;
  khoaHet: boolean;
}) {
  const nhanPhu =
    t.nguon === "db" ? (
      <Chip muc="on" title="Giá trị đang dùng đến từ database — thứ anh dán ở đây">
        đã lưu{t.duoi ? ` · …${t.duoi}` : ""}
      </Chip>
    ) : t.nguon === "env" ? (
      <Chip muc="canh" title={`Đang dùng biến ${t.bienEnv ?? ""} trong .env`}>
        từ .env
      </Chip>
    ) : (
      <Chip muc={t.batBuoc ? "loi" : "thuong"}>{t.batBuoc ? "còn thiếu" : "chưa đặt"}</Chip>
    );

  return (
    <div className="min-w-0">
      <div className="mb-1 flex flex-wrap items-baseline gap-2">
        <label className="text-sm font-medium text-muc-800" htmlFor={`o-${t.khoa}`}>
          {t.nhan}
        </label>
        {nhanPhu}
        {t.nguon === "db" && (
          <button
            type="button"
            className="text-xs text-muc-500 underline underline-offset-2 hover:text-loi-600"
            onClick={onXoa}
            disabled={khoaHet}
          >
            xoá
          </button>
        )}
      </div>

      {t.nhieuDong ? (
        <textarea
          id={`o-${t.khoa}`}
          className={`${lopO} h-28 font-mono text-xs`}
          value={giaTri}
          disabled={khoaHet}
          spellCheck={false}
          placeholder={t.hienDuoc ?? t.goiY}
          onChange={(e) => onDoi(e.target.value)}
        />
      ) : (
        <input
          id={`o-${t.khoa}`}
          className={lopO}
          // `type="password"` cho bí mật: màn hình này bị chụp và chia sẻ.
          type={t.biMat ? "password" : "text"}
          autoComplete="off"
          spellCheck={false}
          value={giaTri}
          disabled={khoaHet}
          // Trường KHÔNG bí mật hiện giá trị thật làm placeholder — URL API và chat id là thứ
          // cần nhìn thấy để sửa. Bí mật thì chỉ có gợi ý.
          placeholder={t.hienDuoc ?? t.goiY}
          onChange={(e) => onDoi(e.target.value)}
        />
      )}

      <p className="mt-1 text-xs text-muc-500">
        {t.goiY}
        {t.bienEnv && <span className="ml-1 text-muc-400">· hoặc {t.bienEnv} trong .env</span>}
      </p>
    </div>
  );
}

/* ═══ Facebook — đổi token ngắn lấy Page token dài hạn ════════════════ */

function KhoiFacebook({
  h,
  onXong,
  khoaHet,
}: {
  h: HangKetNoiUI;
  onXong: () => void;
  khoaHet: boolean;
}) {
  const toast = useToast();
  const [tokenNgan, datTokenNgan] = useState("");
  const [trang, datTrang] = useState<{ id: string; ten: string; token: string }[] | null>(null);

  /**
   * ⚠️ **`h.truong` từ API đã GỘP `truongTuSinh` vào**, và `pageId` / `pageTokenEnc` đều
   * `batBuoc`. Duyệt thẳng mọi trường bắt buộc ở đây là khoá ô nhập bằng chính thứ ô nhập này
   * sinh ra — bế tắc đã xảy ra thật trên production 10/09/2026. `duNhapTay` là câu hỏi đúng, và
   * nó có test ở `packages/shared/test/ket-noi.test.ts`.
   */
  const coApp = duNhapTay(h.truong);
  const pageId = h.truong.find((t) => t.khoa === "ketnoi.facebook.pageId");

  const doi = useMutation({
    mutationFn: () => api.fbDoiToken(tokenNgan),
    onSuccess: (kq) => {
      datTokenNgan("");
      datTrang(kq.trang);
      // Đúng một trang thì chọn luôn — một danh sách một dòng bắt người ta bấm thêm một lần
      // để chọn thứ duy nhất có.
      if (kq.trang.length === 1 && kq.trang[0]) chon.mutate(kq.trang[0]);
      else toast.on(`Thấy ${kq.trang.length} trang`, "Chọn fanpage sẽ đăng bài.");
    },
    onError: (loi: Error) => toast.loi("Đổi token hỏng", loi.message),
  });

  const chon = useMutation({
    mutationFn: (t: { id: string; token: string }) => api.fbChonTrang(t.id, t.token),
    onSuccess: (kq) => {
      datTrang(null);
      if (kq.ok) toast.on("Facebook — đã nối", kq.chiTiet);
      else toast.loi("Đã lưu Page token, nhưng kiểm KHÔNG đạt", kq.loi ?? undefined);
      onXong();
    },
    onError: (loi: Error) => toast.loi("Không lưu được trang", loi.message),
  });

  return (
    <div className="mt-4 rounded-tile border border-muc-200 bg-muc-50 p-3">
      <h4 className="text-sm font-semibold text-muc-800">Page token</h4>

      {pageId?.co ? (
        <p className="mt-1 text-sm text-muc-600">
          Đang dùng trang <strong>{pageId.hienDuoc}</strong>. Đổi token mới khi Meta thu hồi, hoặc
          khi đổi sang fanpage khác.
        </p>
      ) : (
        <p className="mt-1 text-sm text-muc-600">
          Chưa có. Dán một <strong>User token ngắn</strong> lấy ở Graph API Explorer — AutoIME đổi
          nó sang Page token dài hạn. Token ngắn <strong>không</strong> được lưu lại.
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <input
            className={lopO}
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={tokenNgan}
            disabled={khoaHet || !coApp}
            placeholder={coApp ? "EAAB…" : "Lưu App ID và App Secret trước đã"}
            onChange={(e) => datTokenNgan(e.target.value)}
          />
        </div>
        <Nut
          dangChay={doi.isPending}
          disabled={khoaHet || !coApp || tokenNgan.trim().length < 20}
          onClick={() => doi.mutate()}
        >
          Đổi token
        </Nut>
      </div>

      {trang && trang.length > 1 && (
        <div className="mt-3">
          <p className="mb-1 text-sm font-medium text-muc-800">Chọn fanpage sẽ đăng bài</p>
          <ul className="space-y-1">
            {trang.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-sm text-muc-700" title={t.ten}>
                  {t.ten} <span className="text-muc-400">· {t.id}</span>
                </span>
                <Nut dangChay={chon.isPending} disabled={khoaHet} onClick={() => chon.mutate(t)}>
                  Chọn
                </Nut>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
